import { t } from 'i18next';
import { html, nothing, TemplateResult } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';

import { iconCircleInfo } from '@/components/icons';
import { DateType } from '@/types';
import { classMap } from 'lit/directives/class-map.js';

/**
 * Transforms a locale code into a format compatible with `toLocaleDateString`.
 *
 * @param locale - The locale code to transform.
 * @returns The transformed locale code.
 */
const transformLocale = (locale: string) => {
  // transform locale code into toLocaleDateString compatible format
  return locale.replace('_', '-');
};

/**
 * Formats a given date string according to the specified locale and type.
 *
 * @param date - The date string to format.
 * @param locale - The locale to use for formatting the date.
 * @param type - The format type for the month (optional). It can be 'long', 'numeric', or 'short'.
 * @returns The formatted date string or an empty string if the date is invalid.
 */
export const formatDate = (
  date: string,
  locale: string,
  type?: 'long' | 'numeric' | 'short',
) => {
  if (!isDateValid(date)) {
    return '';
  }

  const convertedDate = new Date(date),
    transformedLocale = transformLocale(locale);

  return convertedDate.toLocaleDateString(
    transformedLocale === 'en-US' ? undefined : transformedLocale,
    {
      year: 'numeric',
      month: type || 'long',
      day: 'numeric',
    },
  );
};

/**
 * Checks if a given date string is valid.
 *
 * @param dateString - The date string to validate. It can be a string, null, or undefined.
 * @returns A boolean indicating whether the date string is valid.
 */
export const isDateValid = (dateString: string | null | undefined) => {
  if (!dateString) {
    return false;
  }
  return !isNaN(Date.parse(dateString));
};

/**
 * Generates a template for displaying an order date with optional information popup.
 *
 * @param date - The date to be displayed.
 * @param locale - The locale to format the date.
 * @param type - The type of date (shipping or delivery).
 * @param showPopup - Whether to show the information popup.
 * @param hspEnabled - Whether HSP (High-Speed Processing) is enabled.
 * @param earliestDeliveryDate - The earliest delivery date if available to render before there is a date
 * @param blockRender - Should it render as a block with a background or as a single line text
 * @returns The template result for the order date.
 */
export const elephantOrderDateTemplate = (
  date: string | null | undefined,
  locale: string,
  type: DateType = DateType.SHIPPING,
  showPopup?: boolean,
  hspEnabled = false,
  earliestDeliveryDate?: string | null,
  blockRender: boolean = false,
): TemplateResult | typeof nothing => {
  if (!date && !earliestDeliveryDate) {
    return nothing;
  }

  const format = type === DateType.SHIPPING ? 'short' : 'long';

  const formattedDate = formatDate(
    (date || earliestDeliveryDate) ?? '',
    locale,
    format,
  );

  const showEarliestDeliveryDate = Boolean(!date && earliestDeliveryDate);

  const refSpan = createRef();

  const infoButton = html`
    <button
      class="${classMap({
        'info-shipping-date': type === DateType.SHIPPING,
        'info-delivery-date': type === DateType.DELIVERY,
      })}"
      @click="${(event: Event) => {
        event.stopPropagation();

        if (showEarliestDeliveryDate) {
          window.dispatchEvent(
            new CustomEvent('open-earliest-delivery-date-modal'),
          );
          return;
        }

        window.dispatchEvent(
          new CustomEvent('show-delivery-date-info', {
            detail: {
              hspEnabled,
            },
          }),
        );
      }}"
    >
      <span class="sr-only">
        ${type === DateType.SHIPPING
          ? t('actions.openModalWithMoreInfoAboutShippingDate')
          : t('actions.openModalWithMoreInfoAboutExpectedDeliveryDate')}
      </span>
      ${iconCircleInfo}
    </button>
  `;

  return html`
    <div
      class="shipping-date ${classMap({
        'expected-delivery-container': type === DateType.DELIVERY,
        'delivery-block-container': blockRender,
      })}"
      ${ref(refSpan)}
    >
      ${!formattedDate
        ? showEarliestDeliveryDate
          ? earliestDeliveryDate
          : date
        : html`
            ${type === DateType.SHIPPING
              ? html`
                  <span class="expected-shipping-content">
                    <span>${t('labels.expectedShippingDate')}:</span>
                    <date>
                      <span>${formattedDate}</span>
                      ${showPopup ? infoButton : nothing}
                    </date>
                  </span>
                `
              : html`
                  <div
                    class="expected-delivery-content ${classMap({
                      'delivery-block-content': blockRender,
                    })}"
                  >
                    <span>
                      ${t('content.configurator.expectedDeliveryDate')}
                    </span>
                    <span class="info-and-formatted-date">
                      <strong>
                        ${showEarliestDeliveryDate
                          ? t('labels.fromDate', { date: formattedDate })
                          : formattedDate}
                      </strong>
                      ${infoButton}
                    </span>
                  </div>
                `}
          `}
    </div>
  `;
};
/**
 * @param timeString
 * @param locale
 * @returns Converted time string (hour:minute) based on the current locale
 */
export const formatTimeString = (
  timeString: string,
  locale: string,
): string => {
  const date = new Date(timeString);

  return date.toLocaleTimeString(transformLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * A utility function that checks if the current given time string is earlier or later that he current date
 *
 * @param {string} timeString
 * @returns Returns a boolean that determines if the current time is later or earlier than the cutoff time
 */
export const afterCutoffTime = (timeString: string) => {
  const currentDate = new Date(),
    cutoffDate = new Date(timeString);

  if (
    cutoffDate > currentDate &&
    currentDate.getDay() === cutoffDate.getDay()
  ) {
    return false;
  }
  return true;
};
